import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getRaffle, createEntry, getSettings, getMyEntries, createCheckout, getStripePublicConfig } from '../lib/api';
import { redirectToPayment } from '../lib/paymentRedirect';
import { useAuth } from '../lib/auth';
import { useLang } from '../lib/lang';
import CountdownTimer from '../components/CountdownTimer';
import InlineEditable from '../components/InlineEditable';
import ShareButtons from '../components/ShareButtons';
import Footer from '../components/Footer';
import PromoCodeInput from '../components/PromoCodeInput';
import PayWithCryptoButton from '../components/PayWithCryptoButton';
import ExpressParticipateModal from '../components/ExpressParticipateModal';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Clock, Ticket, Minus, Plus, ArrowLeft, ShieldCheck, Truck, FileText, Users, Trophy, ChevronDown } from 'lucide-react';

export default function RaffleDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const { t } = useLang();
  const [raffle, setRaffle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState(1);
  const [entering, setEntering] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [questionError, setQuestionError] = useState('');
  const [pointRate, setPointRate] = useState(1);
  const [payMode, setPayMode] = useState('points');
  const [selectedPackage, setSelectedPackage] = useState(null); // null = custom qty, index = package // 'points' or 'direct'
  const [myEntries, setMyEntries] = useState([]);
  const [stripeEnabled, setStripeEnabled] = useState(false);
  const [paying, setPaying] = useState(false);
  const [creditToUse, setCreditToUse] = useState(0);  // Partial credit applied at checkout
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [expressOpen, setExpressOpen] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const [r, s, sp] = await Promise.all([getRaffle(id), getSettings(), getStripePublicConfig()]);
        setRaffle(r.data.raffle);
        setPointRate(s.data.settings?.point_rate || 1);
        setStripeEnabled(sp.data?.enabled === true);
        // Load user's existing entries on this raffle (for max_per_user calc)
        if (user) {
          try {
            const me = await getMyEntries();
            const entriesForThis = (me.data.entries || []).filter(e => (e.raffle_id === id) || (e.raffle?._id === id));
            setMyEntries(entriesForThis);
          } catch {}
        }
      }
      catch { toast.error('Raffle not found'); navigate('/'); }
      finally { setLoading(false); }
    }
    load();
  }, [id, navigate, user]);

  const handleEnter = async () => {
    if (!user) { navigate('/login'); return; }
    setEntering(true);
    setQuestionError('');
    try {
      const payload = { raffle_id: id, quantity: qty };
      if (selectedPackage !== null) payload.package_idx = selectedPackage;
      if (raffle.question?.enabled) {
        if (selectedAnswer === null) { toast.error(t('questionRequired')); setEntering(false); return; }
        payload.answer = String(selectedAnswer);
      }
      await createEntry(payload);
      toast.success(`Successfully entered ${qty} time(s)!`);
      setDialogOpen(false);
      setSelectedAnswer(null);
      await refreshUser();
      const res = await getRaffle(id);
      setRaffle(res.data.raffle);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to enter draw';
      if (msg.includes('Incorrect answer')) {
        setQuestionError(t('incorrectAnswer'));
        setSelectedAnswer(null);
      } else {
        toast.error(msg);
      }
    }
    finally { setEntering(false); }
  };

  // Direct payment via Stripe Checkout (no points used)
  const handleDirectPay = async () => {
    if (!user) { navigate('/login'); return; }
    if (!stripeEnabled) {
      toast.info(t('stripeComingSoon') || 'Paiements bientôt disponibles');
      return;
    }
    if (raffle.question?.enabled) {
      if (selectedAnswer === null) { toast.error(t('questionRequired') || 'Question requise'); return; }
    }
    setPaying(true);
    try {
      const payload = {
        type: 'raffle_direct',
        raffle_id: id,
        quantity: qty,
        origin_url: window.location.origin,
        credit_to_use: creditToUse || 0,
        promo_code: appliedPromo?.code || undefined,
      };
      if (selectedPackage !== null) payload.package_idx = selectedPackage;
      if (raffle.question?.enabled) payload.answer = String(selectedAnswer);
      const res = await createCheckout(payload);
      // If credit fully covered the price, no Stripe redirect needed
      if (res.data?.credit_only) {
        toast.success(`Participation enregistrée ! Tickets : ${res.data.ticket_numbers?.join(', ')}`);
        await refreshUser();
        setDialogOpen(false);
        navigate('/account');
        setPaying(false);
        return;
      }
      const ok = await redirectToPayment(res.data);
      if (!ok) { toast.error('URL de paiement manquante'); setPaying(false); }
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Erreur de paiement';
      if (msg.toLowerCase().includes('incorrect answer')) {
        setQuestionError(t('incorrectAnswer'));
        setSelectedAnswer(null);
      } else {
        toast.error(msg);
      }
      setPaying(false);
    }
  };

  if (loading || !raffle) return <div className="min-h-screen flex items-center justify-center"><div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  const realSold = raffle.sold_tickets || 0;
  const max = raffle.max_tickets || 1;
  const displayedSold = (raffle.displayed_sold_tickets !== null && raffle.displayed_sold_tickets !== undefined)
    ? raffle.displayed_sold_tickets
    : realSold;
  const isSoldOut = (raffle.force_sold_out === true) || (realSold >= max);
  const isComingSoon = raffle.status === 'coming_soon';
  const hasAdvancedDrawDate = isSoldOut && !!raffle.draw_date;
  const sold = isSoldOut ? max : displayedSold;
  const pct = Math.round((sold / max) * 100);
  // `remaining` is used to CAP the qty selector. It must be based on the REAL
  // sold count, not the (possibly inflated) admin-overridden display value,
  // otherwise the +/- buttons silently snap qty back down to 0 when
  // displayedSold >= max while realSold < max.
  const remaining = isSoldOut ? 0 : Math.max(0, max - realSold);
  const maxPerUser = parseInt(raffle.max_tickets_per_user || 0);
  const userTicketsBought = (myEntries || []).reduce((sum, e) => sum + (e.quantity || 0), 0);
  const userRemainingAllowed = maxPerUser > 0 ? Math.max(0, maxPerUser - userTicketsBought) : null;
  const exceedsUserLimit = maxPerUser > 0 && (userTicketsBought + qty > maxPerUser);
  const totalCost = raffle.ticket_cost_points * qty;
  const discountPct = selectedPackage !== null && raffle.ticket_packages?.[selectedPackage] ? raffle.ticket_packages[selectedPackage].discount_pct : 0;
  const discountedCost = discountPct > 0 ? Math.round(totalCost * (1 - discountPct / 100)) : totalCost;
  const totalGBP = (discountedCost / (pointRate || 1)).toFixed(2);
  const pricePerEntryGBP = (raffle.ticket_cost_points / (pointRate || 1)).toFixed(2);
  const hasEnded = raffle.ends_at && new Date(raffle.ends_at) < new Date();

  return (
    <div className="min-h-screen" data-testid="raffle-detail">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button variant="ghost" onClick={() => navigate('/')} className="gap-2 mb-6 text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4" /> <InlineEditable tKey="backToDraws">{t('backToDraws')}</InlineEditable>
        </Button>

        {/* SOLD OUT banner at the top */}
        {isSoldOut && (
          <div className="mb-6 rounded-2xl overflow-hidden border-2 border-red-400/50 bg-gradient-to-r from-red-700 via-red-600 to-red-700 shadow-[0_8px_28px_rgba(0,0,0,0.30)]" data-testid="raffle-detail-sold-out-banner">
            <div className="relative px-6 py-4 flex items-center gap-3">
              <div className="absolute inset-0 noise-overlay opacity-30" />
              <div className="relative w-12 h-12 rounded-full bg-white/15 border-2 border-white/40 flex items-center justify-center shrink-0">
                <span className="text-2xl">🚫</span>
              </div>
              <div className="relative flex-1">
                <p className="font-display text-xl sm:text-2xl font-extrabold text-white tracking-tight uppercase leading-tight">
                  <InlineEditable tKey="soldOutBig">{t('soldOutBig') || 'All Tickets Sold'}</InlineEditable>
                </p>
                <p className="text-xs sm:text-sm text-white/85 mt-0.5">
                  {hasAdvancedDrawDate ? (
                    <InlineEditable tKey="soldOutAdvancedDrawWithDate">{t('soldOutAdvancedDrawWithDate') || 'Tirage avancé ! Découvrez la nouvelle date ci-dessous.'}</InlineEditable>
                  ) : (
                    <InlineEditable tKey="soldOutAdvancedDrawPending">{t('soldOutAdvancedDrawPending') || 'Tirage avancé suite au sold out. Date à venir prochainement — revenez bientôt !'}</InlineEditable>
                  )}
                </p>
              </div>
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          <div data-testid="raffle-detail-gallery">
            <div className="rounded-2xl overflow-hidden bg-[#F8F6F0] shadow-[0_20px_60px_rgba(0,0,0,0.08)] mb-4">
              <img src={raffle.images?.[selectedImage] || raffle.images?.[0]} alt={raffle.title} className="w-full aspect-square object-cover" />
            </div>
            {raffle.images?.length > 1 && (
              <div className="flex gap-3">
                {raffle.images.map((img, i) => (
                  <button key={i} onClick={() => setSelectedImage(i)} className={`w-20 h-20 rounded-xl overflow-hidden border-2 transition-colors ${i === selectedImage ? 'border-primary' : 'border-border hover:border-primary/40'}`}>
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="lg:sticky lg:top-24 lg:self-start" data-testid="raffle-purchase-panel">
            <Card className="bg-white border-border shadow-[0_1px_0_rgba(20,22,26,0.04),0_12px_30px_rgba(20,22,26,0.06)]">
              <CardContent className="p-5 sm:p-6 space-y-3.5">
                {/* ── Title + value ─── */}
                <div>
                  <Badge className="mb-2 bg-accent text-accent-foreground border-primary/20 uppercase text-[10px] tracking-[0.18em] font-semibold">{raffle.category}</Badge>
                  <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{raffle.title}</h1>
                  {!isComingSoon && (
                    <p className="font-mono text-lg font-bold text-primary mt-1.5"><InlineEditable tKey="worth">{t('worth')}</InlineEditable> £{raffle.product_value?.toLocaleString()}</p>
                  )}
                </div>

                {/* ── Countdown / Sold Out advanced draw ─── */}
                {!isComingSoon && (
                  isSoldOut ? (
                    hasAdvancedDrawDate ? (
                      <div className="p-3 rounded-xl bg-[hsl(var(--rolex-green-deep))] border border-[hsl(var(--gold)/0.30)]" data-testid="raffle-detail-advanced-countdown" style={{ color: 'hsl(43 38% 92%)' }}>
                        <CountdownTimer endDate={raffle.draw_date} mode="card" soldOut />
                      </div>
                    ) : (
                      <div className="p-4 rounded-xl bg-gradient-to-br from-amber-50 via-orange-50 to-red-50 border-2 border-amber-300 text-center" data-testid="raffle-detail-advanced-pending">
                        <p className="text-[11px] uppercase tracking-[0.25em] font-bold text-amber-700 mb-1.5 flex items-center justify-center gap-1.5">
                          🎯 <InlineEditable tKey="advancedDrawLabel">{t('advancedDrawLabel') || 'Tirage avancé'}</InlineEditable>
                        </p>
                        <p className="font-display text-lg sm:text-xl font-bold text-rolex-green leading-tight">
                          <InlineEditable tKey="advancedDrawComingSoon">{t('advancedDrawComingSoon') || 'Date à venir prochainement'}</InlineEditable>
                        </p>
                        <p className="text-[11px] text-rolex-green/65 mt-1.5 italic">
                          <InlineEditable tKey="advancedDrawComingSoonHelp">{t('advancedDrawComingSoonHelp') || 'Repassez bientôt pour découvrir la nouvelle date du tirage.'}</InlineEditable>
                        </p>
                      </div>
                    )
                  ) : (
                    raffle.ends_at && (
                      <div className="p-3 rounded-xl bg-[hsl(var(--rolex-green-deep))] border border-[hsl(var(--gold)/0.30)]" data-testid="raffle-detail-countdown" style={{ color: 'hsl(43 38% 92%)' }}>
                        <CountdownTimer endDate={raffle.ends_at} mode="card" soldOut={false} />
                      </div>
                    )
                  )
                )}

                {/* ── Compact stats row: Max participants + Max winners ─── */}
                <div className="grid grid-cols-2 gap-2" data-testid="raffle-detail-limits">
                  <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-[#0E4936] via-[#0A3825] to-[#08291F] border border-gold/60 px-3 py-2.5 shadow-md" style={{ color: '#F5EFE0' }}>
                    <div className="flex items-center gap-1 mb-0.5">
                      <Users className="w-3 h-3" style={{ color: '#D4AF37' }} />
                      <p className="text-[9px] uppercase tracking-[0.15em] font-extrabold" style={{ color: '#D4AF37' }}>
                        <InlineEditable tKey="maxParticipants">{t('maxParticipants') || 'Participants'}</InlineEditable>
                      </p>
                    </div>
                    <p className="font-mono text-xl font-black text-gold-gradient tabular-nums leading-none">
                      {(raffle.max_tickets || 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="relative overflow-hidden rounded-xl bg-gold-gradient px-3 py-2.5 shadow-md">
                    <div className="flex items-center gap-1 mb-0.5">
                      <Trophy className="w-3 h-3" style={{ color: '#0B3D2E' }} />
                      <p className="text-[9px] uppercase tracking-[0.15em] font-extrabold" style={{ color: '#0B3D2E' }}>
                        <InlineEditable tKey="maxWinnersLabel">{t('maxWinnersLabel') || 'Gagnants'}</InlineEditable>
                      </p>
                    </div>
                    <p className="font-mono text-xl font-black tabular-nums leading-none" style={{ color: '#0B3D2E' }}>
                      {raffle.max_winners || 1}
                    </p>
                  </div>
                </div>

                {/* ── Max per user — compact 1-line banner ─── */}
                {maxPerUser > 0 && (
                  <div className="rounded-lg bg-blue-50 border border-blue-200 px-3 py-2 text-xs flex items-center gap-2" data-testid="raffle-detail-max-per-user">
                    <span className="shrink-0 w-6 h-6 rounded-full bg-blue-500 text-white font-bold text-[10px] flex items-center justify-center">{maxPerUser}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-blue-900 leading-tight">
                        <strong>Max {maxPerUser} {(t('ticketsLowercase') || 'tickets')} / joueur</strong>
                        {user && userTicketsBought > 0 && (
                          <span className="text-blue-700"> · <InlineEditable tKey="boughtLabel">{t('boughtLabel') || 'achetés'}</InlineEditable> : <strong>{userTicketsBought}</strong>{userRemainingAllowed > 0 ? <> · <InlineEditable tKey="remainingLabel">{t('remainingLabel') || 'reste'}</InlineEditable> : <strong className="text-green-700">{userRemainingAllowed}</strong></> : <> · <strong className="text-red-600"><InlineEditable tKey="limitReached">{t('limitReached') || 'limite atteinte'}</InlineEditable></strong></>}</span>
                        )}
                      </p>
                    </div>
                  </div>
                )}

                {/* ── Progress bar (compact) ─── */}
                {!isComingSoon && (
                  <div className="space-y-1" data-testid="raffle-detail-progress">
                    <div className="flex justify-between text-[10px]">
                      <span className="text-muted-foreground uppercase tracking-wider font-semibold"><InlineEditable tKey="totalTicketsAvailable">{t('totalTicketsAvailable') || 'Tickets disponibles'}</InlineEditable></span>
                      <span className="font-mono font-bold text-primary">{max.toLocaleString()}</span>
                    </div>
                    <div className="progress-gold"><Progress value={pct} className="h-2 bg-muted" /></div>
                  </div>
                )}

                {/* ── Ticket price (highlight banner) ─── */}
                {!isComingSoon && (
                  <div className="px-3 py-2 rounded-xl bg-accent/30 border border-primary/10 flex items-center justify-between">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold"><InlineEditable tKey="ticketPrice">{t('ticketPrice') || 'Prix du ticket'}</InlineEditable></p>
                    <div className="flex items-baseline gap-1.5">
                      <Ticket className="w-4 h-4 text-primary" />
                      <span className="font-mono text-2xl font-extrabold text-primary">£{pricePerEntryGBP}</span>
                    </div>
                  </div>
                )}
                {/* Ticket Packages + Custom Quantity Selector */}
                {!hasEnded && !isSoldOut && !isComingSoon && (
                  <div className="space-y-3">
                    <p className="text-xs font-semibold text-foreground uppercase tracking-wider">
                      <InlineEditable tKey="chooseTickets">{t('chooseTickets') || 'Choisir vos tickets'}</InlineEditable>
                    </p>

                    {/* Quick quantity buttons (1, 2, 3, 5, 10) + custom */}
                    <div className="flex flex-wrap gap-2">
                      {[1, 2, 3, 5, 10].filter(n => n <= remaining).map(n => {
                        // Check if this number matches a package — if so, mark with discount badge
                        const matchingPkg = (raffle.ticket_packages || []).findIndex(p => p.qty === n);
                        const hasDiscount = matchingPkg !== -1 && raffle.ticket_packages[matchingPkg].discount_pct > 0;
                        const dPct = hasDiscount ? raffle.ticket_packages[matchingPkg].discount_pct : 0;
                        const isActive = qty === n;
                        return (
                          <button
                            key={n}
                            type="button"
                            onClick={() => {
                              setQty(n);
                              // Auto-select package if quantity matches a package qty
                              setSelectedPackage(matchingPkg !== -1 ? matchingPkg : null);
                            }}
                            className={`relative px-5 py-3 rounded-xl border-2 font-bold text-sm transition-all min-w-[64px] ${
                              isActive
                                ? 'border-primary bg-primary text-primary-foreground shadow-md scale-105'
                                : 'border-border bg-white hover:border-primary/50 text-foreground'
                            }`}
                            data-testid={`qty-quick-${n}`}
                          >
                            <span className="font-mono text-lg">{n}</span>
                            <span className="block text-[9px] uppercase tracking-wider opacity-75 -mt-0.5">
                              {n === 1 ? (t('ticket') || 'ticket') : (t('tickets') || 'tickets')}
                            </span>
                            {hasDiscount && (
                              <span className="absolute -top-2 -right-2 bg-emerald-500 text-white text-[9px] font-extrabold px-1.5 py-0.5 rounded-full shadow-md">
                                -{dPct}%
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>

                    {/* Custom quantity selector */}
                    <div className="flex items-center gap-3 mt-2 pt-3 border-t border-dashed border-border">
                      <p className="text-xs text-muted-foreground whitespace-nowrap">
                        <InlineEditable tKey="orChooseAmount">{t('orChooseAmount') || 'Ou choisissez :'}</InlineEditable>
                      </p>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-9 w-9 rounded-lg"
                          onClick={() => {
                            const newQty = Math.max(1, qty - 1);
                            setQty(newQty);
                            const matchingPkg = (raffle.ticket_packages || []).findIndex(p => p.qty === newQty);
                            setSelectedPackage(matchingPkg !== -1 ? matchingPkg : null);
                          }}
                          data-testid="qty-decrement"
                        >
                          <Minus className="w-4 h-4" />
                        </Button>
                        <input
                          type="number"
                          min={1}
                          max={remaining}
                          value={qty}
                          onChange={(e) => {
                            const v = Math.min(remaining, Math.max(1, parseInt(e.target.value) || 1));
                            setQty(v);
                            const matchingPkg = (raffle.ticket_packages || []).findIndex(p => p.qty === v);
                            setSelectedPackage(matchingPkg !== -1 ? matchingPkg : null);
                          }}
                          className="font-mono text-lg font-bold text-center w-20 h-9 border border-border rounded-lg focus:outline-none focus:border-primary"
                          data-testid="qty-input"
                        />
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-9 w-9 rounded-lg"
                          onClick={() => {
                            const newQty = Math.min(remaining, qty + 1);
                            setQty(newQty);
                            const matchingPkg = (raffle.ticket_packages || []).findIndex(p => p.qty === newQty);
                            setSelectedPackage(matchingPkg !== -1 ? matchingPkg : null);
                          }}
                          data-testid="qty-increment"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Discount applied banner */}
                    {selectedPackage !== null && raffle.ticket_packages?.[selectedPackage]?.discount_pct > 0 && (
                      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-center gap-2 animate-fadeIn">
                        <span className="text-xl">🎉</span>
                        <div className="flex-1">
                          <p className="text-sm font-bold text-emerald-800">
                            <InlineEditable tKey="discountApplied">{t('discountApplied') || 'Réduction appliquée !'}</InlineEditable>
                          </p>
                          <p className="text-xs text-emerald-700">
                            -{raffle.ticket_packages[selectedPackage].discount_pct}% sur {raffle.ticket_packages[selectedPackage].qty} tickets
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Show available packages directly (always visible) */}
                    {raffle.ticket_packages?.length > 0 && (
                      <div className="mt-3 space-y-2">
                        <p className="text-xs font-semibold text-foreground/70 uppercase tracking-wider">
                          📦 <InlineEditable tKey="availablePacks">{t('availablePacks') || 'Packs disponibles'}</InlineEditable>
                        </p>
                        <div className="grid grid-cols-1 gap-2">
                          {raffle.ticket_packages.map((pkg, idx) => {
                            const pkgTotal = raffle.ticket_cost_points * pkg.qty;
                            const pkgDiscounted = Math.round(pkgTotal * (1 - pkg.discount_pct / 100));
                            const pkgGBP = (pkgDiscounted / (pointRate || 1)).toFixed(2);
                            // Express odds as "1 in X" (1 chance sur X) — more intuitive than %
                            const oddsOneIn = pkg.qty > 0 ? Math.max(1, Math.round(max / pkg.qty)) : max;
                            const isSelected = selectedPackage === idx && qty === pkg.qty;
                            const bestValue = pkg.discount_pct === Math.max(...raffle.ticket_packages.map(p => p.discount_pct));
                            const pkgDisabled = isSoldOut || pkg.qty > remaining;
                            return (
                              <button key={idx} disabled={pkgDisabled} onClick={() => { if (pkgDisabled) return; setSelectedPackage(idx); setQty(pkg.qty); }} data-testid={`pkg-${idx}`} className={`relative text-left p-3 rounded-xl border transition-colors ${pkgDisabled ? 'opacity-40 grayscale cursor-not-allowed border-border' : isSelected ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-border hover:border-primary/40'}`}>
                                {pkgDisabled && <span className="absolute -top-2 right-3 bg-muted text-muted-foreground text-[10px] font-bold px-2 py-0.5 rounded-full border border-border" data-testid={`pkg-unavailable-${idx}`}><InlineEditable tKey="unavailable">{t('unavailable') || 'Indisponible'}</InlineEditable></span>}
                                {!pkgDisabled && bestValue && pkg.discount_pct > 0 && <span className="absolute -top-2 right-3 bg-primary text-primary-foreground text-[10px] font-bold px-2 py-0.5 rounded-full">BEST VALUE</span>}
                                <div className="flex items-center justify-between">
                                  <div>
                                    <span className="font-semibold text-sm">{pkg.qty} <InlineEditable tKey="tickets">{t('tickets')}</InlineEditable></span>
                                    {pkg.discount_pct > 0 && <span className="ml-2 text-xs text-green-600 font-semibold">-{pkg.discount_pct}%</span>}
                                  </div>
                                  <div className="text-right">
                                    {pkg.discount_pct > 0 && <span className="text-xs text-muted-foreground line-through mr-2">£{pkgTotal}</span>}
                                    <span className="font-mono font-bold text-primary">£{pkgGBP}</span>
                                  </div>
                                </div>
                                <p className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1">
                                  <span className="font-mono font-bold text-emerald-700">1 / {oddsOneIn.toLocaleString()}</span>
                                  <InlineEditable tKey="chanceToWin">{t('chanceToWin') || 'chance de gagner'}</InlineEditable>
                                </p>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {isComingSoon ? (
                  <div className="text-center py-6 px-4 rounded-2xl bg-amber-50 border-2 border-amber-300">
                    <div className="text-5xl mb-3">⏳</div>
                    <h3 className="font-display text-2xl font-extrabold text-amber-700 mb-2">
                      <InlineEditable tKey="comingSoon">{t('comingSoon') || 'Bientôt disponible'}</InlineEditable>
                    </h3>
                    <p className="text-sm text-amber-800/85 leading-relaxed">
                      <InlineEditable tKey="comingSoonDetailHint" multiline>{t('comingSoonDetailHint') || 'Ce tirage sera bientôt ouvert à la participation. Revenez prochainement !'}</InlineEditable>
                    </p>
                  </div>
                ) : !hasEnded && !isSoldOut ? (
                    <Button
                      className="w-full bg-primary text-primary-foreground hover:bg-primary/90 btn-press text-base h-14 rounded-xl shadow-[0_1px_0_rgba(20,22,26,0.06),0_10px_24px_rgba(184,138,42,0.18)]"
                      size="lg"
                      onClick={() => setExpressOpen(true)}
                      data-testid="raffle-participate-button"
                    >
                      <InlineEditable tKey="participate">{t('participate') || 'Participer'}</InlineEditable>
                    </Button>
                ) : (
                  <Button className="w-full rounded-xl" size="lg" disabled>{hasEnded ? t('drawEnded') : t('soldOut')}</Button>
                )}
                {false && (
                  <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogTrigger asChild>
                      <div className="space-y-2">
                        <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90 btn-press text-base h-14 rounded-xl shadow-[0_1px_0_rgba(20,22,26,0.06),0_10px_24px_rgba(184,138,42,0.18)]" size="lg" data-testid="raffle-enter-now-button">
                          <InlineEditable tKey="enterDraw">{t('enterDraw')}</InlineEditable>
                        </Button>
                      </div>
                    </DialogTrigger>
                    <DialogContent className="bg-white border-border max-h-[90vh] overflow-y-auto p-0" data-testid="raffle-confirm-dialog">
                      {/* ── Sticky header with total ───────────── */}
                      <div className="sticky top-0 z-10 bg-gradient-to-b from-cream to-cream/95 backdrop-blur-sm border-b border-gold/20 px-5 py-4">
                        <DialogHeader className="text-left">
                          <DialogTitle className="font-display text-lg leading-tight">
                            <InlineEditable tKey="confirmEntry">{t('confirmEntry')}</InlineEditable>
                          </DialogTitle>
                          <DialogDescription className="text-xs text-rolex-green/65">{raffle.title}</DialogDescription>
                        </DialogHeader>
                      </div>

                      <div className="px-5 pt-3 pb-4 space-y-3">
                        {/* ── 1. Question first (if enabled — MANDATORY before payment) ── */}
                        {raffle.question?.enabled && (
                          <div className="bg-amber-50 rounded-xl p-3 border-2 border-amber-200">
                            <p className="text-[10px] uppercase tracking-wider font-bold text-amber-800 mb-2 flex items-center gap-1">
                              ⚠️ <InlineEditable tKey="answerRequired">{t('answerRequired') || 'Question obligatoire'}</InlineEditable>
                            </p>
                            {raffle.question.image && (
                              <img src={raffle.question.image} alt="Question" className="w-full max-h-28 object-contain rounded-lg bg-white mb-2" />
                            )}
                            <p className="font-semibold text-sm mb-2">{raffle.question.question}</p>
                            <div className="grid grid-cols-1 gap-1.5">
                              {raffle.question.answers?.map((ans, idx) => (
                                <button
                                  key={idx}
                                  onClick={() => { setSelectedAnswer(idx); setQuestionError(''); }}
                                  className={`text-left px-3 py-2 rounded-lg border text-sm transition-colors ${selectedAnswer === idx ? 'border-emerald-500 bg-emerald-50 font-semibold' : 'border-stone-200 bg-white hover:border-amber-400'}`}
                                  data-testid={`question-answer-${idx}`}
                                >
                                  {ans}
                                </button>
                              ))}
                            </div>
                            {questionError && <p className="text-red-500 text-xs font-medium mt-1.5">{questionError}</p>}
                          </div>
                        )}

                        {/* ── 2. Quantity selector — BIG and obvious ── */}
                        <div className="bg-rolex-green-deep rounded-2xl p-4 text-cream">
                          <p className="text-[10px] uppercase tracking-[0.22em] font-extrabold text-gold mb-2 text-center">
                            <InlineEditable tKey="quantity">{t('quantity')}</InlineEditable>
                          </p>
                          <div className="flex items-center justify-center gap-5">
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => setQty(Math.max(1, qty - 1))}
                              className="rounded-full h-12 w-12 bg-cream/10 border-gold/40 text-cream hover:bg-gold/20 hover:text-gold shadow-lg"
                              data-testid="qty-minus"
                            >
                              <Minus className="w-5 h-5" />
                            </Button>
                            <span className="font-mono text-4xl font-extrabold w-20 text-center text-gold" data-testid="raffle-ticket-quantity-select">{qty}</span>
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => setQty(Math.min(remaining, qty + 1))}
                              className="rounded-full h-12 w-12 bg-cream/10 border-gold/40 text-cream hover:bg-gold/20 hover:text-gold shadow-lg"
                              data-testid="qty-plus"
                            >
                              <Plus className="w-5 h-5" />
                            </Button>
                          </div>
                          {/* Quick presets */}
                          {remaining >= 5 && (
                            <div className="flex justify-center gap-1.5 mt-3 flex-wrap">
                              {[1, 3, 5, 10, 25].filter((n) => n <= remaining).map((n) => (
                                <button
                                  key={n}
                                  type="button"
                                  onClick={() => setQty(n)}
                                  className={`text-[10px] px-2.5 py-1 rounded-full border transition-colors font-bold ${qty === n ? 'bg-gold-gradient text-rolex-green border-transparent' : 'bg-cream/10 border-cream/30 text-cream/70 hover:bg-cream/20'}`}
                                >
                                  ×{n}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* ── 3. Total à payer (big number) ── */}
                        <div className="bg-gold-gradient text-rolex-green rounded-2xl p-4 shadow-lg">
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <p className="text-[10px] uppercase tracking-[0.22em] font-extrabold opacity-80">
                                <InlineEditable tKey="totalToPay">{t('totalToPay') || 'Total à payer'}</InlineEditable>
                              </p>
                              <p className="font-mono text-3xl font-black leading-none mt-1">
                                £{Math.max(0, Number(totalGBP) - Number(creditToUse) - Number(appliedPromo?.final_discount || 0)).toFixed(2)}
                              </p>
                            </div>
                            <div className="text-right text-xs opacity-80">
                              <p className="font-mono">£{pricePerEntryGBP} × {qty}</p>
                              {(creditToUse > 0 || appliedPromo?.final_discount > 0) && (
                                <p className="font-mono mt-0.5">
                                  {creditToUse > 0 && <>−£{creditToUse} crédit </>}
                                  {appliedPromo?.final_discount > 0 && <>−£{Number(appliedPromo.final_discount).toFixed(0)} promo</>}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* ── 4. Pay buttons (visible without scrolling) ── */}
                        <div className="space-y-2 sm:flex sm:gap-2 sm:space-y-0">
                          <Button
                            className="bg-primary text-primary-foreground hover:bg-primary/90 btn-press rounded-xl w-full h-14 text-base font-bold flex-1"
                            onClick={handleDirectPay}
                            disabled={paying || !user}
                            data-testid="raffle-direct-pay-btn"
                          >
                            {paying ? '...' : (() => {
                              const discount = appliedPromo?.final_discount || 0;
                              const toPay = Math.max(0, Number(totalGBP) - Number(creditToUse) - Number(discount));
                              if (toPay <= 0) return <>✅ <InlineEditable tKey="confirmEntry">{t('confirmEntry')}</InlineEditable></>;
                              return <>💳 <InlineEditable tKey="payByCard">{t('payByCard') || 'Payer par carte'}</InlineEditable></>;
                            })()}
                          </Button>
                          <PayWithCryptoButton
                            amountGbp={Math.max(0, Number(totalGBP) - Number(creditToUse) - Number(appliedPromo?.final_discount || 0))}
                            payload={{
                              type: 'raffle_direct',
                              raffle_id: id,
                              quantity: qty,
                              credit_to_use: creditToUse || 0,
                              promo_code: appliedPromo?.code || undefined,
                              package_idx: selectedPackage !== null ? selectedPackage : undefined,
                              answer: raffle.question?.enabled ? String(selectedAnswer) : undefined,
                            }}
                            disabled={paying || !user || (raffle.question?.enabled && selectedAnswer === null)}
                            className="rounded-xl w-full sm:w-auto h-14 text-base font-bold sm:flex-1"
                          />
                        </div>

                        {/* ── 5. Expandable additional options ── */}
                        <details className="group rounded-xl bg-stone-50 border border-stone-200 overflow-hidden">
                          <summary className="px-4 py-3 cursor-pointer flex items-center justify-between text-sm font-semibold text-rolex-green hover:bg-stone-100 transition-colors list-none">
                            <span className="flex items-center gap-2">
                              ⚙️ <InlineEditable tKey="moreOptions">{t('moreOptions') || 'Plus d\u2019options (crédit, code promo, détails)'}</InlineEditable>
                            </span>
                            <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" />
                          </summary>
                          <div className="p-4 pt-1 space-y-3 border-t border-stone-200 bg-white">
                            {/* Credit-use toggle */}
                            {user && user.points_balance > 0 && (
                              <button
                                type="button"
                                onClick={() => setCreditToUse(creditToUse > 0 ? 0 : Math.min(user.points_balance, totalGBP))}
                                className={`w-full p-3 rounded-xl border-2 text-left transition-all flex items-center gap-3 ${
                                  creditToUse > 0 ? 'border-emerald-500 bg-emerald-50' : 'border-stone-200 hover:border-emerald-300 bg-white'
                                }`}
                                data-testid="use-credit-toggle"
                              >
                                <div className={`shrink-0 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors ${
                                  creditToUse > 0 ? 'bg-emerald-600 border-emerald-600' : 'border-stone-300 bg-white'
                                }`}>
                                  {creditToUse > 0 && (
                                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-bold text-sm text-rolex-green"><InlineEditable tKey="useMyCredit">{t('useMyCredit') || 'Utiliser mon crédit'}</InlineEditable></p>
                                  <p className="text-xs text-rolex-green/65 mt-0.5">
                                    <InlineEditable tKey="balance">{t('balance') || 'Solde'}</InlineEditable> : <strong className="text-emerald-700">£{user.points_balance}</strong>
                                    {creditToUse > 0 && <span className="ml-2 text-emerald-700 font-bold">→ −£{Math.min(user.points_balance, totalGBP)}</span>}
                                  </p>
                                </div>
                              </button>
                            )}

                            {/* Promo code field */}
                            <PromoCodeInput
                              variant="light"
                              applied={appliedPromo}
                              onApplied={setAppliedPromo}
                              context={{ raffle_id: id, amount: Number(totalGBP) }}
                            />

                            {/* Pricing breakdown */}
                            <div className="bg-cream rounded-xl p-3 space-y-1 border border-gold/15">
                              <div className="flex justify-between text-xs">
                                <span className="text-rolex-green/65"><InlineEditable tKey="pricePerEntry">{t('pricePerEntry')}</InlineEditable></span>
                                <span className="font-mono font-bold">£{pricePerEntryGBP}</span>
                              </div>
                              <div className="flex justify-between text-xs">
                                <span className="text-rolex-green/65"><InlineEditable tKey="quantity">{t('quantity')}</InlineEditable></span>
                                <span className="font-mono">×{qty}</span>
                              </div>
                              {discountPct > 0 && (
                                <div className="flex justify-between text-xs text-emerald-600">
                                  <span><InlineEditable tKey="discount">{t('discount')}</InlineEditable></span>
                                  <span className="font-mono">−{discountPct}%</span>
                                </div>
                              )}
                              {appliedPromo?.final_discount > 0 && (
                                <div className="flex justify-between text-xs text-emerald-600">
                                  <span>Remise ({appliedPromo.code})</span>
                                  <span className="font-mono">−£{Number(appliedPromo.final_discount).toFixed(2)}</span>
                                </div>
                              )}
                              {creditToUse > 0 && (
                                <div className="flex justify-between text-xs text-amber-700">
                                  <span>Crédit utilisé</span>
                                  <span className="font-mono">−£{Number(creditToUse).toFixed(2)}</span>
                                </div>
                              )}
                              <div className="border-t border-gold/20 pt-1.5 mt-1.5 flex justify-between font-bold text-sm">
                                <span className="text-rolex-green"><InlineEditable tKey="total">{t('total')}</InlineEditable></span>
                                <span className="font-mono text-emerald-700">£{Math.max(0, Number(totalGBP) - Number(creditToUse) - Number(appliedPromo?.final_discount || 0)).toFixed(2)}</span>
                              </div>
                            </div>
                          </div>
                        </details>

                        {/* Cancel link (subtle) */}
                        <button
                          type="button"
                          onClick={() => setDialogOpen(false)}
                          className="w-full text-xs text-rolex-green/55 hover:text-rolex-green/80 pt-1 underline-offset-2 hover:underline"
                        >
                          <InlineEditable tKey="cancel">{t('cancel')}</InlineEditable>
                        </button>
                      </div>
                    </DialogContent>
                  </Dialog>
                )}
              </CardContent>
            </Card>
            {/* Share buttons */}
            <div className="mt-4 p-4 bg-white border border-border rounded-xl">
              <ShareButtons raffleId={raffle._id} raffleTitle={raffle.title} />
            </div>
            <Tabs defaultValue="details" className="mt-6">
              <TabsList className="w-full bg-white border border-border">
                <TabsTrigger value="details" className="flex-1 gap-1 text-xs"><FileText className="w-3 h-3" /> <InlineEditable tKey="details">{t('details')}</InlineEditable></TabsTrigger>
                <TabsTrigger value="shipping" className="flex-1 gap-1 text-xs"><Truck className="w-3 h-3" /> <InlineEditable tKey="shipping">{t('shipping')}</InlineEditable></TabsTrigger>
                <TabsTrigger value="rules" className="flex-1 gap-1 text-xs"><ShieldCheck className="w-3 h-3" /> <InlineEditable tKey="rules">{t('rules')}</InlineEditable></TabsTrigger>
              </TabsList>
              <TabsContent value="details" className="mt-4">
                <Card className="bg-white border-border">
                  <CardContent className="p-5 space-y-4">
                    <p className="text-sm text-muted-foreground leading-relaxed">{raffle.description}</p>
                    {raffle.specs && Object.keys(raffle.specs).length > 0 && (
                      <div className="border-t border-border pt-4">
                        <h4 className="font-display font-bold text-sm mb-3 uppercase tracking-wider text-foreground"><InlineEditable tKey="specifications">{t('specifications')}</InlineEditable></h4>
                        <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                          {Object.entries(raffle.specs).filter(([_, v]) => v).map(([key, value]) => {
                            const specLabels = {
                              brand: t('specBrand'), model: t('specModel'), reference_number: t('specRefNumber'), movement: t('specMovement'),
                              year: t('specYear'), caliber: t('specCaliber'), glass: t('specGlass'), bezel_material: t('specBezelMaterial'),
                              bracelet_material: t('specBraceletMaterial'), papers: t('specPapers'), reference: t('specReference'),
                              material: t('specMaterial'), color: t('specColor'), size: t('specSize'), hardware: t('specHardware'),
                              type: t('specType'), stones: t('specStones'), carat: t('specCarat'), certification: t('specCertification'),
                              items_included: t('specItemsIncluded'), storage: t('specStorage'), warranty: t('specWarranty'),
                              destination: t('specDestination'), duration: t('specDuration'), hotel: t('specHotel'),
                              stars: t('specStars'), included: t('specIncluded'), departure_from: t('specDepartureFrom'),
                              valid_until: t('specValidUntil'), engine: t('specEngine'), mileage: t('specMileage'),
                              transmission: t('specTransmission'), details: t('specDetails'),
                            };
                            return (
                              <div key={key} className="flex justify-between py-1.5 border-b border-border/50">
                                <span className="text-xs text-muted-foreground">{specLabels[key] || key.replace(/_/g, ' ')}</span>
                                <span className="text-xs font-semibold text-foreground text-right max-w-[60%] whitespace-pre-line">{value}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    <div className="border-t border-border pt-3 grid grid-cols-2 gap-3 text-xs">
                      <div className="flex justify-between"><span className="text-muted-foreground"><InlineEditable tKey="maximumEntries">{t('maximumEntries')}</InlineEditable></span><span className="font-mono font-bold">{max.toLocaleString()}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground"><InlineEditable tKey="maxWinners">{t('maxWinners')}</InlineEditable></span><span className="font-mono font-bold">{raffle.max_winners || 1}</span></div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
              <TabsContent value="shipping" className="mt-4"><Card className="bg-white border-border"><CardContent className="p-5"><p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">{raffle.shipping_info || t('shippingInfo')}</p></CardContent></Card></TabsContent>
              <TabsContent value="rules" className="mt-4"><Card className="bg-white border-border"><CardContent className="p-5"><p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">{raffle.rules_info || `This is a skill-based competition. Entrants must be 18+ and UK residents. A maximum of ${max.toLocaleString()} entries are available. The winner will be selected by random draw and notified within 48 hours.`}</p></CardContent></Card></TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
      <ExpressParticipateModal
        open={expressOpen}
        onOpenChange={setExpressOpen}
        raffle={raffle}
        qty={qty}
        selectedPackage={selectedPackage}
        pricePerEntryGBP={pricePerEntryGBP}
        totalGBP={totalGBP}
        appliedPromo={appliedPromo}
        setAppliedPromo={setAppliedPromo}
        stripeEnabled={stripeEnabled}
        onLoggedIn={refreshUser}
      />
      <Footer />
    </div>
  );
}
